/**
 * ตัวตนของ "เครื่อง" และ "คนที่ยืนอยู่หน้าเครื่อง" สำหรับ envelope ของคิวออฟไลน์
 *
 * `enqueueOfflineCommand` บังคับให้มีทั้ง `device_id` และ `employee_id`
 * (`commandEnvelope.ts`) แต่ที่ HEAD นี้ **ยังไม่มีแหล่งจริงของทั้งสองค่าในเครื่อง**:
 *
 *   - `device_id` ต้องมาจากการลงทะเบียนอุปกรณ์ — ตาราง `device_registration`
 *     เพิ่งมีใน migration ของ M11 (D-074) และ "โปรโตคอลพิสูจน์ตัวตน/หมุนคีย์เป็น
 *     WO ของ device-auth แยก" ยังไม่มี endpoint ให้เรียก หัวจอของ shell จึงยัง
 *     เขียนตามจริงว่า "ยังไม่ผูกอุปกรณ์กับไลน์ผลิต"
 *   - `employee_id` ไม่ได้อยู่ในเครื่องเลย: session ของแอปเก็บ **token เปล่า ๆ
 *     ค่าเดียว** (`lib/auth/tokenStorage.ts` — "คีย์เดียว ค่าเดียว") และ
 *     `GET /v1/me/permissions` ซึ่งเป็นที่เดียวที่รู้ `employee_id` ถูกกันออกจาก
 *     เส้นทางของเครื่องสแกนโดยตั้งใจ เพื่อให้เปิดหน้าจอได้ตอน WAN หลุด
 *     (`App.tsx`) — เครื่องที่ต้องทำงานออฟไลน์จะไปดึงค่านี้ตอนกด submit ไม่ได้
 *
 * ทั้งสองค่าจึงเป็น **placeholder ที่เขียนไว้ที่เดียว** ให้ Work Order ของ
 * device registration / operator session มาแทนที่ในฟังก์ชันเดียวนี้ ไม่ต้องไล่แก้
 * ตามหน้าจอ และ **ห้ามแก้ด้วยการเปิดช่องให้ผู้ปฏิบัติงานพิมพ์เอง**: รหัสพนักงาน
 * ที่พิมพ์เองคือ attribution ที่ปลอมได้ ซึ่งแย่กว่าค่าที่บอกตรง ๆ ว่ายังไม่รู้
 *
 * ที่ยอมส่งของชั่วคราวไปได้โดยไม่ทำให้บัญชีผิด เพราะทั้งสองค่านี้อยู่บน
 * **envelope ไม่ใช่ payload**: ผู้กระทำจริงที่ถูกบันทึกลง `operation_confirmation`
 * มาจาก auth context ฝั่ง server เสมอ (`started_by_employee_id` /
 * `completed_by_employee_id` ใน `docs/API_CONTRACT.md`) ไม่ใช่จากค่าที่เครื่อง
 * ส่งขึ้นไป — ค่าที่นี่ใช้เพื่อ attribution/ลำดับของอุปกรณ์เท่านั้น
 * (`commandEnvelope.ts`: "ใช้เพื่อ attribution เท่านั้น ไม่ใช่ authentication")
 *
 * ⚠️ ห้ามเอา session token มาใส่ค่าใดค่าหนึ่งเด็ดขาด — คิวในเครื่องที่หายไปพร้อม
 * เครื่องต้องไม่ถือ credential (`docs/SECURITY.md`)
 */

export interface ShopFloorIdentity {
  readonly device_id: string;
  readonly employee_id: string;
}

/**
 * ค่าชั่วคราวที่อ่านออกว่า "ยังไม่ได้ลงทะเบียน" ไม่ใช่รหัสที่ดูเหมือนของจริง
 *
 * ตั้งใจให้เห็นได้ทันทีทั้งบนหน้าจอและใน conflict queue ฝั่ง server ว่าคำสั่งนี้
 * มาจากเครื่องที่ยังไม่ผูกทะเบียน จะได้ไม่มีใครเผลออ่านเป็นอุปกรณ์จริงเครื่องหนึ่ง
 */
export const UNREGISTERED_DEVICE_ID = 'UNREGISTERED-DEVICE';

/** ค่าชั่วคราวของผู้ปฏิบัติงาน ด้วยเหตุผลเดียวกับ `UNREGISTERED_DEVICE_ID` */
export const UNIDENTIFIED_OPERATOR_ID = 'UNIDENTIFIED-OPERATOR';

/**
 * ตัวตนที่จะถูกแนบไปกับคำสั่งใบถัดไป
 *
 * **นี่คือจุดเดียวที่ต้องแก้** เมื่อ device registration และแหล่งของ `employee_id`
 * ฝั่งเครื่องมีจริง — หน้าจอทั้งสามเรียกผ่านฟังก์ชันนี้เท่านั้น
 */
export function readShopFloorIdentity(): ShopFloorIdentity {
  return {
    device_id: UNREGISTERED_DEVICE_ID,
    employee_id: UNIDENTIFIED_OPERATOR_ID,
  };
}

/** อุปกรณ์/ผู้ปฏิบัติงานยังเป็นค่าชั่วคราวอยู่หรือไม่ — หน้าจอใช้บอกผู้ใช้ตามจริง */
export function isPlaceholderIdentity(identity: ShopFloorIdentity): boolean {
  return (
    identity.device_id === UNREGISTERED_DEVICE_ID ||
    identity.employee_id === UNIDENTIFIED_OPERATOR_ID
  );
}
